/**
 * Détection des navigateurs in-app (WebView intégrées à WhatsApp, Facebook,
 * Instagram, etc.) dans lesquels Google refuse l'OAuth depuis 2021
 * (erreur 403 `disallowed_useragent`).
 *
 * Doc Google : https://developers.google.com/identity/protocols/oauth2/policies#secure-browsers
 *
 * Le seul correctif côté nous est d'afficher au user une instruction
 * claire avant qu'il clique sur "Continuer avec Google" → "Ouvre l'app
 * dans Chrome / Safari".
 */

export interface InAppDetection {
  isInApp: boolean;
  /** Nom de l'app détectée (WhatsApp, Facebook, …) ou null si UA générique. */
  appName: string | null;
  /** Plateforme inférée pour adapter les instructions de sortie. */
  platform: "ios" | "android" | "other";
}

interface UAPattern {
  name: string;
  pattern: RegExp;
}

const IN_APP_PATTERNS: UAPattern[] = [
  // Ordre = spécificité décroissante
  { name: "WhatsApp", pattern: /WhatsApp/i },
  { name: "Facebook", pattern: /FBAN|FBAV|FB_IAB|FB4A|FBSV|FBBV/ },
  { name: "Messenger", pattern: /Messenger|MessengerLite/i },
  { name: "Instagram", pattern: /Instagram/ },
  { name: "TikTok", pattern: /musical_ly|TikTok|BytedanceWebview|Aweme/i },
  { name: "Twitter", pattern: /TwitterAndroid|Twitter for/i },
  { name: "LinkedIn", pattern: /LinkedInApp/i },
  { name: "Snapchat", pattern: /Snapchat/i },
  { name: "Telegram", pattern: /Telegram/ },
  { name: "Line", pattern: /\bLine\//i },
  { name: "WeChat", pattern: /MicroMessenger/i },
];

function detectPlatform(ua: string): InAppDetection["platform"] {
  if (/iPhone|iPad|iPod/.test(ua)) return "ios";
  if (/Android/.test(ua)) return "android";
  return "other";
}

export function detectInAppBrowser(): InAppDetection {
  if (typeof navigator === "undefined") {
    return { isInApp: false, appName: null, platform: "other" };
  }
  const ua = navigator.userAgent ?? "";
  const platform = detectPlatform(ua);

  for (const p of IN_APP_PATTERNS) {
    if (p.pattern.test(ua)) {
      return { isInApp: true, appName: p.name, platform };
    }
  }

  // Marqueur générique des WebView Android intégrées (Chrome Custom Tab
  // ouvert depuis une app, etc.). Le user-agent contient explicitement
  // "; wv)" — voir https://developer.chrome.com/docs/multidevice/user-agent
  if (platform === "android" && /;\s*wv\)/.test(ua)) {
    return { isInApp: true, appName: null, platform };
  }

  return { isInApp: false, appName: null, platform };
}

/**
 * Copie l'URL courante dans le presse-papier. Retourne true si OK.
 * Fallback sur execCommand si l'API Clipboard n'est pas dispo (anciens
 * navigateurs intégrés).
 */
export async function copyCurrentUrl(): Promise<boolean> {
  if (typeof window === "undefined") return false;
  const url = window.location.href;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(url);
      return true;
    }
  } catch {
    /* fallback below */
  }
  try {
    const el = document.createElement("textarea");
    el.value = url;
    el.style.position = "fixed";
    el.style.opacity = "0";
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(el);
    return ok;
  } catch {
    return false;
  }
}
